/**
 * VTOContextStorage - Virtual Try-On Context Storage Utility
 * Handles preservation and restoration of VTO state during authentication flows
 */
class VTOContextStorage {
  constructor() {
    this.storageKey = 'vto_auth_context';
    this.defaultTTL = 60 * 60 * 1000; // 1 hour in milliseconds
    this.maxContextSize = 5 * 1024 * 1024; // 5MB max context size
  }

  /**
   * Store VTO context in sessionStorage with expiration
   * @param {Object} context - VTO context to store
   * @param {number} ttl - Time to live in milliseconds (optional)
   * @returns {boolean} Success status
   */
  storeContext(context, ttl = this.defaultTTL) {
    try {
      // Validate and sanitize context
      const sanitizedContext = this.sanitizeContext(context);
      
      if (!sanitizedContext) {
        console.error('VTOContextStorage: Invalid context provided');
        return false;
      }

      // Create storage object with metadata
      const storageObject = {
        context: sanitizedContext,
        timestamp: Date.now(),
        expiresAt: Date.now() + ttl,
        version: '1.0'
      };

      // Check size constraints
      const serialized = JSON.stringify(storageObject);
      if (serialized.length > this.maxContextSize) {
        console.error('VTOContextStorage: Context size exceeds maximum limit');
        return false;
      }

      // Store in sessionStorage
      sessionStorage.setItem(this.storageKey, serialized);
      
      console.log('VTOContextStorage: Context stored successfully');
      return true;

    } catch (error) {
      console.error('VTOContextStorage: Failed to store context:', error);
      return false;
    }
  }

  /**
   * Retrieve and validate stored VTO context
   * @returns {Object|null} Retrieved context or null if not found/expired
   */
  retrieveContext() {
    try {
      const stored = sessionStorage.getItem(this.storageKey);
      
      if (!stored) {
        return null;
      }

      const storageObject = JSON.parse(stored);
      
      // Check expiration
      if (Date.now() > storageObject.expiresAt) {
        console.log('VTOContextStorage: Context expired, cleaning up');
        this.clearContext();
        return null;
      }

      // Validate context structure
      if (!this.validateContext(storageObject.context)) {
        console.error('VTOContextStorage: Invalid context structure');
        this.clearContext();
        return null;
      }

      console.log('VTOContextStorage: Context retrieved successfully');
      return storageObject.context;

    } catch (error) {
      console.error('VTOContextStorage: Failed to retrieve context:', error);
      this.clearContext();
      return null;
    }
  }

  /**
   * Clear stored VTO context
   */
  clearContext() {
    try {
      sessionStorage.removeItem(this.storageKey);
      console.log('VTOContextStorage: Context cleared');
    } catch (error) {
      console.error('VTOContextStorage: Failed to clear context:', error);
    }
  }

  /**
   * Check if context exists and is valid
   * @returns {boolean} True if valid context exists
   */
  hasValidContext() {
    const context = this.retrieveContext();
    return context !== null;
  }

  /**
   * Get context metadata without retrieving full context
   * @returns {Object|null} Context metadata or null
   */
  getContextMetadata() {
    try {
      const stored = sessionStorage.getItem(this.storageKey);
      
      if (!stored) {
        return null;
      }

      const storageObject = JSON.parse(stored);
      
      return {
        timestamp: storageObject.timestamp,
        expiresAt: storageObject.expiresAt,
        version: storageObject.version,
        isExpired: Date.now() > storageObject.expiresAt,
        timeRemaining: Math.max(0, storageObject.expiresAt - Date.now())
      };

    } catch (error) {
      console.error('VTOContextStorage: Failed to get metadata:', error);
      return null;
    }
  }

  /**
   * Sanitize context data to prevent XSS and ensure data integrity
   * @param {Object} context - Raw context to sanitize
   * @returns {Object|null} Sanitized context or null if invalid
   */
  sanitizeContext(context) {
    if (!context || typeof context !== 'object') {
      return null;
    }

    try {
      const sanitized = {
        personImage: this.sanitizePersonImage(context.personImage),
        canvasItems: this.sanitizeCanvasItems(context.canvasItems),
        selectedItem: this.sanitizeString(context.selectedItem),
        mode: this.sanitizeMode(context.mode),
        operation: this.sanitizeOperation(context.operation)
      };

      // Remove any undefined values
      Object.keys(sanitized).forEach(key => {
        if (sanitized[key] === undefined) {
          delete sanitized[key];
        }
      });

      return sanitized;

    } catch (error) {
      console.error('VTOContextStorage: Context sanitization failed:', error);
      return null;
    }
  }

  /**
   * Sanitize person image data
   * @param {Object} personImage - Person image data
   * @returns {Object|undefined} Sanitized person image data
   */
  sanitizePersonImage(personImage) {
    if (!personImage || typeof personImage !== 'object') {
      return undefined;
    }

    const sanitized = {};

    // Sanitize image data (base64 string)
    if (typeof personImage.image === 'string' && personImage.image.startsWith('data:image/')) {
      sanitized.image = personImage.image;
    }

    // Sanitize metadata
    if (personImage.metadata && typeof personImage.metadata === 'object') {
      sanitized.metadata = {
        width: this.sanitizeNumber(personImage.metadata.width),
        height: this.sanitizeNumber(personImage.metadata.height),
        size: this.sanitizeNumber(personImage.metadata.size),
        type: this.sanitizeString(personImage.metadata.type)
      };
    }

    // Sanitize position
    if (personImage.position && typeof personImage.position === 'object') {
      sanitized.position = {
        x: this.sanitizeNumber(personImage.position.x),
        y: this.sanitizeNumber(personImage.position.y)
      };
    }

    // Sanitize scale
    if (typeof personImage.scale === 'number') {
      sanitized.scale = Math.max(0.1, Math.min(5, personImage.scale));
    }

    return Object.keys(sanitized).length > 0 ? sanitized : undefined;
  }

  /**
   * Sanitize canvas items array
   * @param {Array} canvasItems - Canvas items array
   * @returns {Array|undefined} Sanitized canvas items
   */
  sanitizeCanvasItems(canvasItems) {
    if (!Array.isArray(canvasItems)) {
      return undefined;
    }

    const sanitized = canvasItems
      .filter(item => item && typeof item === 'object')
      .map(item => ({
        id: this.sanitizeString(item.id),
        x: this.sanitizeNumber(item.x),
        y: this.sanitizeNumber(item.y),
        width: this.sanitizeNumber(item.width),
        height: this.sanitizeNumber(item.height),
        rotation: this.sanitizeNumber(item.rotation),
        scale: this.sanitizeNumber(item.scale),
        zIndex: this.sanitizeNumber(item.zIndex)
      }))
      .filter(item => item.id); // Only keep items with valid IDs

    return sanitized.length > 0 ? sanitized : undefined;
  }

  /**
   * Sanitize operation data
   * @param {Object} operation - Operation data
   * @returns {Object|undefined} Sanitized operation data
   */
  sanitizeOperation(operation) {
    if (!operation || typeof operation !== 'object') {
      return undefined;
    }

    return {
      type: this.sanitizeString(operation.type),
      itemId: this.sanitizeString(operation.itemId),
      timestamp: this.sanitizeNumber(operation.timestamp)
    };
  }

  /**
   * Sanitize mode value
   * @param {string} mode - Mode value
   * @returns {string|undefined} Sanitized mode
   */
  sanitizeMode(mode) {
    const validModes = ['virtual', 'manual', 'hybrid'];
    return validModes.includes(mode) ? mode : undefined;
  }

  /**
   * Sanitize string value
   * @param {any} value - Value to sanitize
   * @returns {string|undefined} Sanitized string
   */
  sanitizeString(value) {
    if (typeof value !== 'string') {
      return undefined;
    }
    
    // Basic XSS prevention - remove script tags and javascript: protocols
    const cleaned = value
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/javascript:/gi, '')
      .trim();
    
    return cleaned.length > 0 ? cleaned : undefined;
  }

  /**
   * Sanitize numeric value
   * @param {any} value - Value to sanitize
   * @returns {number|undefined} Sanitized number
   */
  sanitizeNumber(value) {
    const num = parseFloat(value);
    return !isNaN(num) && isFinite(num) ? num : undefined;
  }

  /**
   * Validate context structure
   * @param {Object} context - Context to validate
   * @returns {boolean} True if valid structure
   */
  validateContext(context) {
    if (!context || typeof context !== 'object') {
      return false;
    }

    // At least one of the main properties should exist
    const hasPersonImage = context.personImage && typeof context.personImage === 'object';
    const hasCanvasItems = Array.isArray(context.canvasItems);
    const hasMode = typeof context.mode === 'string';
    const hasOperation = context.operation && typeof context.operation === 'object';

    return hasPersonImage || hasCanvasItems || hasMode || hasOperation;
  }

  /**
   * Cleanup expired contexts (can be called periodically)
   */
  static cleanupExpiredContexts() {
    try {
      const keys = Object.keys(sessionStorage);
      const vtoKeys = keys.filter(key => key.startsWith('vto_'));
      
      vtoKeys.forEach(key => {
        try {
          const stored = sessionStorage.getItem(key);
          if (stored) {
            const data = JSON.parse(stored);
            if (data.expiresAt && Date.now() > data.expiresAt) {
              sessionStorage.removeItem(key);
              console.log(`VTOContextStorage: Cleaned up expired context: ${key}`);
            }
          }
        } catch (error) {
          // Remove corrupted entries
          sessionStorage.removeItem(key);
          console.log(`VTOContextStorage: Removed corrupted context: ${key}`);
        }
      });

    } catch (error) {
      console.error('VTOContextStorage: Cleanup failed:', error);
    }
  }
}

export default VTOContextStorage;